import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Calendar, Clock, ArrowRight } from 'lucide-react';
import { articles, Article } from '../data/articles';
import { AnimatedSection } from '../components/AnimatedSection';
export function ArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const article = articles.find(a => a.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center px-4" data-id="element-211">
        <h1 className="text-4xl font-heading text-primary-900 mb-4" data-id="element-212">
          Article Not Found
        </h1>
        <p className="text-warm-gray-600 font-body mb-8" data-id="element-213">
          The resource you are looking for doesn't exist or has been moved.
        </p>
        <button onClick={() => navigate('/resources')} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-800 text-warm-white rounded-full font-body font-medium hover:bg-primary-700 transition-colors" data-id="element-214">
          <ArrowLeft className="w-4 h-4" data-id="element-215" /> Back to Resources
        </button>
      </div>;
  }
  const relatedArticles = articles.filter(a => a.cat === article.cat && a.slug !== article.slug).slice(0, 3);
  // If not enough related in same category, just grab some from the same type
  const displayRelated = relatedArticles.length > 0 ? relatedArticles : articles.filter(a => a.type === article.type && a.slug !== article.slug).slice(0, 3);
  return <div className="w-full bg-warm-white pb-24" data-id="element-216">
      {/* HERO SECTION */}
      <div className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-217">
        <div className="absolute inset-0 z-0" data-id="element-218">
          <img src={article.img} alt={article.title} className="w-full h-full object-cover object-center" data-id="element-219" />
          <div className="absolute inset-0 bg-primary-900/80 backdrop-blur-sm" data-id="element-220"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-221">
          <Link to="/resources" className="inline-flex items-center gap-2 text-primary-100 hover:text-warm-white font-body text-sm mb-8 transition-colors" data-id="element-222">
            <ArrowLeft className="w-4 h-4" data-id="element-223" /> Back to Resources
          </Link>

          <div className="flex flex-wrap items-center gap-4 mb-6" data-id="element-224">
            <span className="px-3 py-1 rounded-full bg-accent-500 text-primary-900 text-xs font-bold font-body flex items-center gap-1" data-id="element-225">
              <Tag className="w-3 h-3" data-id="element-226" /> {article.cat}
            </span>
            {article.date && <span className="text-primary-100 text-sm font-body flex items-center gap-1" data-id="element-227">
                <Calendar className="w-4 h-4" data-id="element-228" /> {article.date}
              </span>}
            <span className="text-primary-100 text-sm font-body flex items-center gap-1" data-id="element-229">
              <Clock className="w-4 h-4" data-id="element-230" /> {article.readTime}
            </span>
          </div>

          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-heading text-warm-white leading-tight mb-6" data-id="element-231">
            {article.title}
          </h1>
          <p className="text-xl text-primary-100 font-body leading-relaxed" data-id="element-232">
            {article.excerpt}
          </p>
        </div>
      </div>

      {/* ARTICLE BODY */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-233">
        <div className="prose prose-lg prose-green max-w-none" data-id="element-234">
          {article.body.map((block, index) => {
          switch (block.type) {
            case 'paragraph':
              return <p key={index} className="font-body text-warm-gray-700 leading-relaxed mb-6 text-lg" data-id="element-235">
                    {block.text}
                  </p>;
            case 'heading':
              return <h2 key={index} className="font-heading text-3xl text-primary-900 mt-12 mb-6" data-id="element-236">
                    {block.text}
                  </h2>;
            case 'list':
              return <ul key={index} className="space-y-3 mb-8" data-id="element-237">
                    {block.items.map((item, i) => <li key={i} className="flex items-start gap-3 font-body text-warm-gray-700 text-lg" data-id="element-238">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2.5 shrink-0" data-id="element-239"></span>
                        <span data-id="element-240">{item}</span>
                      </li>)}
                  </ul>;
            case 'video':
              return <div key={index} className="aspect-w-16 aspect-h-9 my-10 rounded-2xl overflow-hidden shadow-warm-lg border border-warm-gray-200" data-id="element-241">
                    <iframe src={block.url} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full min-h-[400px]" data-id="element-242"></iframe>
                  </div>;
            case 'callout':
              return <div key={index} className="bg-accent-50 border-l-4 border-accent-500 p-6 rounded-r-2xl my-8 shadow-warm-sm" data-id="element-243">
                    <p className="font-body text-primary-900 font-medium text-lg m-0" data-id="element-244">
                      {block.text}
                    </p>
                  </div>;
            default:
              return null;
          }
        })}
        </div>
      </div>

      {/* RELATED ARTICLES */}
      {displayRelated.length > 0 && <AnimatedSection className="py-16 bg-warm-gray-50 border-t border-warm-gray-200" data-id="element-245">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-246">
            <h2 className="text-3xl font-heading text-primary-900 mb-10 text-center" data-id="element-247">
              Keep Reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-248">
              {displayRelated.map((post, i) => <Link to={`/resources/${post.slug}`} key={i} className="bg-white rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all border border-warm-gray-100 flex flex-col h-full group" data-id="element-249">
                  <div className="relative h-48 overflow-hidden" data-id="element-250">
                    <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-251" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold font-body text-primary-800 flex items-center gap-1" data-id="element-252">
                      <Tag className="w-3 h-3" data-id="element-253" /> {post.cat}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-254">
                    <h3 className="font-heading text-xl text-primary-900 mb-3 group-hover:text-accent-600 transition-colors line-clamp-2" data-id="element-255">
                      {post.title}
                    </h3>
                    <p className="font-body text-warm-gray-600 text-sm leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-256">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-primary-800 font-medium font-body group-hover:text-accent-600 transition-colors mt-auto text-sm" data-id="element-257">
                      Read Article <ArrowRight className="w-4 h-4" data-id="element-258" />
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>
        </AnimatedSection>}

      {/* UPGRADE BRIDGE */}
      <AnimatedSection className="py-20 bg-primary-50" data-id="element-259">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-260">
          <h2 className="text-3xl font-heading text-primary-900 mb-4" data-id="element-261">
            Ready to Go Deeper?
          </h2>
          <p className="text-lg text-warm-gray-600 font-body mb-8" data-id="element-262">
            Reading is a great start. Our structured video course takes you from
            basics to confident responder with step-by-step demonstrations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-263">
            <Link to="/video-course" className="inline-flex justify-center items-center px-8 py-4 border-2 border-primary-800 text-base font-bold rounded-full text-primary-800 bg-transparent hover:bg-primary-100 transition-all" data-id="element-264">
              View Video Course
            </Link>
            <Link to="/programs" className="inline-flex items-center gap-2 text-primary-800 font-bold font-body hover:text-accent-600 transition-colors px-4 py-4" data-id="element-265">
              Explore All Programs <ArrowRight className="w-4 h-4" data-id="element-266" />
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}